import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User } from '@/types';
import { authService } from '@/services/auth/auth-service';
import { queryClient } from '@/services/query-client';
import { savedViewsKeys } from '@/services/query-keys/saved-views-keys';
import { clearStoredFollowedStateId } from '@/utils/inventory/force-collect-storage';
import { resetSavedViewsSelections } from '@/features/saved-views/controller';
import { INVENTORY_SCOPE, TRADES_SCOPE } from '@/features/saved-views/types';
import { useInventoryDataStore } from '@/stores/inventory/inventory-data-store';

interface AuthStore {
  isAuthenticated: boolean;
  user: User | null;
  googleAccessToken: string | null;
  setUser: (user: User | null) => void;
  setGoogleAccessToken: (token: string | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  clearAuth: () => void;
  logout: () => Promise<void>;
}

const getUserFromStorage = (): User | null => {
  try {
    const userData = localStorage.getItem('userInfo');
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

// Shared cleanup for both clearAuth and logout: wipes persisted user data,
// resets cross-feature stores, and drops cached queries tied to the session.
// Any new per-session state should be reset here so both paths stay in sync.
const resetSessionState = () => {
  localStorage.removeItem('userInfo');
  clearStoredFollowedStateId();
  useInventoryDataStore.getState().resetForceCollectState();
  // Clear persisted saved-view selection of every scope (inventory, trades —
  // LR-671) so the next user starts on default/system view instead of
  // inheriting the previous user's pick. Goes through the saved-views core, not
  // the scope adapters: importing an adapter here forms an import cycle via
  // saved-views-service → api-client → api-interceptors → this store.
  resetSavedViewsSelections([INVENTORY_SCOPE, TRADES_SCOPE]);
  // Drop cached saved-views lists/details of every scope — otherwise the next
  // user would briefly see the previous user's public views / default id.
  queryClient.removeQueries({ queryKey: savedViewsKeys.all });
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      isAuthenticated: !!getUserFromStorage(),
      user: getUserFromStorage(),
      googleAccessToken: null,

      setUser: (user) => {
        if (user) {
          localStorage.setItem('userInfo', JSON.stringify(user));
        } else {
          localStorage.removeItem('userInfo');
        }
        set({ user, isAuthenticated: !!user });
      },

      setGoogleAccessToken: (token) => set({ googleAccessToken: token }),

      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

      clearAuth: () => {
        resetSessionState();
        set({
          isAuthenticated: false,
          googleAccessToken: null,
          user: null
        });
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch {
          // Clear local state even if server call fails
        }
        resetSessionState();
        set({
          user: null,
          isAuthenticated: false,
          googleAccessToken: null,
        });
      },
    }),
    {
      name: 'auth-store', enabled: import.meta.env.DEV,
    }
  )
);
